import { createContext, useContext, useMemo, useRef } from "react"
import { UseFormSetValue } from "react-hook-form"
import { uniq } from "es-toolkit/array"
import { create, useStore } from "zustand"
import { EpisodeOfCareFragment$key } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { AssignEpisodeOfCareFormType } from "@/modules/patients/detail/progress-note/progress-note-card-header"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { hasTreatmentPlanPdf } from "@/modules/patients/libs/has-treatment-plan-pdf"
import {
  AllCountriesFilterableProgressNoteDocument,
  CH_PROGRESS_NOTE_DOCUMENT_TYPES,
  ChDocumentType,
  DE_PROGRESS_NOTE_DOCUMENT_TYPES,
  DeDocumentType,
} from "@/modules/patients/types/ProgressNoteDocument"
import { safeObjectKeys } from "@/types/SafeObjectKeys"

const defaultFilters: AllProgressNotesFilter = {
  onlyMyNotes: false,
  carePlanSummary: false,
  prescription: false,
  sickLeave: false,
  attestationGuardian: false,
  physiotherapy: false,
  ch_nutritionalPrescription: false,
  psychologicalPsychotherapy: false,
  generalAttestation: false,
  referralLetter: false,
  therapyPrescription: false,
  measLaboratoryPrescription: false,
  ch_measRadiologyPrescription: false,
  medicalReportAttachments: false,
  ch_neuropsychologicDiagnosticPrescription: false,
  ch_laboratoryPrescription: false,
  ch_consultationReport: false,
}

const defaultDocumentFilters: Omit<AllProgressNotesFilter, "onlyMyNotes"> = {
  carePlanSummary: false,
  prescription: false,
  sickLeave: false,
  attestationGuardian: false,
  physiotherapy: false,
  ch_nutritionalPrescription: false,
  psychologicalPsychotherapy: false,
  generalAttestation: false,
  referralLetter: false,
  therapyPrescription: false,
  measLaboratoryPrescription: false,
  ch_measRadiologyPrescription: false,
  medicalReportAttachments: false,
  ch_neuropsychologicDiagnosticPrescription: false,
  ch_laboratoryPrescription: false,
  ch_consultationReport: false,
}

export type AllProgressNotesFilter = {
  onlyMyNotes: boolean
} & { [K in ChDocumentType]: boolean } & { [K in DeDocumentType]: boolean }

interface EpisodeOfCareStoreState {
  progressNoteId: string | null
  progressNoteAndParentData: ProgressNoteAndParentData[]
  progressNotesFilters: Array<keyof AllCountriesFilterableProgressNoteDocument>
  setProgressNoteId: (progressNoteId: string | null) => void
  setProgressNotesData: (progressNotesData: ProgressNoteAndParentData[]) => void

  episodeOfCareFragmentReference: EpisodeOfCareFragment$key | null
  setEpisodeOfCareFragmentReference: (value: EpisodeOfCareFragment$key | null) => void

  assignedEpisodeOfCareIdForTheCurrentProgressNote: string | null
  setAssignedEpisodeOfCareIdForTheCurrentProgressNote: (
    assignedEpisodeOfCareIdForTheCurrentProgressNote: string | null,
  ) => void

  allProgressNotesFilters: AllProgressNotesFilter
  setAllProgressNotesFilters: (filters: Partial<AllProgressNotesFilter>) => void
  resetDocumentFilters: () => void
  resetAllProgressNotesFilters: () => void

  assignEpisodeOfCareForm: UseFormSetValue<AssignEpisodeOfCareFormType> | null
  setAssignEpisodeOfCareForm: (assignEpisodeOfCareForm: UseFormSetValue<AssignEpisodeOfCareFormType>) => void
  initializeProgressNoteFilters: (progressNotesData: ProgressNoteAndParentData[]) => void

  progressNoteIdWithOpenEpisodeOfCareDropdown: string | null
  setProgressNoteIdWithOpenEpisodeOfCareDropdown: (progressNoteId: string | null) => void
}

const extractProgressNoteFilter = (progressNote: ProgressNoteData) => {
  const filterKeys: Array<keyof AllCountriesFilterableProgressNoteDocument> = []
  switch (progressNote.__typename) {
    case "ch_ProgressNote": {
      CH_PROGRESS_NOTE_DOCUMENT_TYPES.forEach((key) => {
        if (
          (key === "carePlanSummary" && hasTreatmentPlanPdf(progressNote)) ||
          (key === "medicalReportAttachments" && progressNote[key]?.edges && progressNote[key]?.edges.length > 0) ||
          (key !== "carePlanSummary" && key !== "medicalReportAttachments" && progressNote[key])
        ) {
          filterKeys.push(key)
        }
      })
      break
    }

    case "de_ProgressNote": {
      DE_PROGRESS_NOTE_DOCUMENT_TYPES.forEach((key) => {
        if (
          (key === "carePlanSummary" && hasTreatmentPlanPdf(progressNote)) ||
          (key === "medicalReportAttachments" && progressNote[key]?.edges && progressNote[key]?.edges.length > 0) ||
          (key !== "carePlanSummary" && key !== "medicalReportAttachments" && progressNote[key])
        ) {
          filterKeys.push(key)
        }
      })
      break
    }
  }

  return filterKeys
}

const createEpisodeOfCareStore = () => {
  return create<EpisodeOfCareStoreState>((set) => ({
    progressNoteId: null,
    progressNoteAndParentData: [],
    progressNotesFilters: [],
    setProgressNoteId: (progressNoteId) => set({ progressNoteId }),
    setProgressNotesData: (progressNotesData) => set({ progressNoteAndParentData: progressNotesData }),

    episodeOfCareFragmentReference: null,
    setEpisodeOfCareFragmentReference: (value) => set({ episodeOfCareFragmentReference: value }),

    assignedEpisodeOfCareIdForTheCurrentProgressNote: null,
    setAssignedEpisodeOfCareIdForTheCurrentProgressNote: (assignedEpisodeOfCareIdForTheCurrentProgressNote) =>
      set({ assignedEpisodeOfCareIdForTheCurrentProgressNote }),

    allProgressNotesFilters: defaultFilters,
    setAllProgressNotesFilters: (filters) =>
      set((state) => {
        const newState = {
          ...state.allProgressNotesFilters,
          ...filters,
        }
        return { allProgressNotesFilters: newState }
      }),
    resetAllProgressNotesFilters: () => set({ allProgressNotesFilters: defaultFilters }),
    resetDocumentFilters: () =>
      set((state) => ({
        allProgressNotesFilters: {
          ...state.allProgressNotesFilters,
          ...defaultDocumentFilters,
        },
      })),
    assignEpisodeOfCareForm: null,
    setAssignEpisodeOfCareForm: (assignEpisodeOfCareForm) => set({ assignEpisodeOfCareForm }),
    initializeProgressNoteFilters(progressNotesData) {
      const progressNotesFilters: Array<keyof AllCountriesFilterableProgressNoteDocument> = []

      progressNotesData.forEach((item) => {
        const { episodeOfCareProgressNoteData: progressNote } = item
        progressNotesFilters.push(...extractProgressNoteFilter(progressNote))
      })

      set({ progressNotesFilters: Array.from(new Set(progressNotesFilters)).sort() })
    },

    progressNoteIdWithOpenEpisodeOfCareDropdown: null,
    setProgressNoteIdWithOpenEpisodeOfCareDropdown: (progressNoteId) =>
      set({ progressNoteIdWithOpenEpisodeOfCareDropdown: progressNoteId }),
  }))
}

type EpisodeOfCareStoreType = ReturnType<typeof createEpisodeOfCareStore>

const EpisodeOfCareStoreContext = createContext<EpisodeOfCareStoreType | null>(null)

type EpisodeOfCareStoreProviderProps = React.PropsWithChildren

export const EpisodeOfCareStoreProvider = ({ children }: EpisodeOfCareStoreProviderProps) => {
  const storeRef = useRef<EpisodeOfCareStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createEpisodeOfCareStore()
  }
  return <EpisodeOfCareStoreContext.Provider value={storeRef.current}>{children}</EpisodeOfCareStoreContext.Provider>
}

export function useEpisodeOfCareStoreContext<T>(selector: (state: EpisodeOfCareStoreState) => T): T {
  const store = useContext(EpisodeOfCareStoreContext)
  if (!store) throw new Error("Missing EpisodeOfCareStoreProvider in the tree")
  return useStore(store, selector)
}

export const useGetExcludedIcdCodes = () => {
  const progressNotesDataStore = useEpisodeOfCareStoreContext((state) => state.progressNoteAndParentData)

  return useMemo(
    () =>
      uniq(
        progressNotesDataStore
          .filter((progressNote) => progressNote.parentEpisodeOfCare.isUpdatable)
          .map((progressNote) => progressNote.parentEpisodeOfCare.icdDiagnosis.diagnosisCode),
      ),
    [progressNotesDataStore],
  )
}

export const useAllProgressNotesFilters = () => {
  const filters = useEpisodeOfCareStoreContext((state) => state.allProgressNotesFilters)

  const hasFilters = safeObjectKeys(filters).some((key) => filters[key] !== defaultFilters[key])

  const hasDocumentFilters = safeObjectKeys(filters).some(
    (key) => key !== "onlyMyNotes" && filters[key] !== defaultFilters[key],
  )

  return { filters, hasFilters, hasDocumentFilters }
}
